import { useCallback, useMemo } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import type { AlertColor } from '@mui/material/Alert';

import {
  IconAlertCircle,
  IconAlertTriangle,
  IconCircleCheck,
  IconInfoCircle,
} from '@tabler/icons-react';
import { useSystemGlobalBanner } from 'contexts/SystemGlobalBannerContext';

/** JSON sample uses MUI-style names; map to Tabler icons from @tabler/icons-react */
const TABLER_ICON_BY_NAME: Record<string, typeof IconInfoCircle> = {
  CheckCircleOutlined: IconCircleCheck,
  InfoOutlined: IconInfoCircle,
  ReportProblemOutlined: IconAlertTriangle,
  ErrorOutline: IconAlertCircle,
  IconCircleCheck,
  IconInfoCircle,
  IconAlertTriangle,
  IconAlertCircle,
};

function parseSeverity(sev: string | undefined): AlertColor {
  if (sev === 'success' || sev === 'info' || sev === 'warning' || sev === 'error') {
    return sev;
  }
  return 'info';
}

export default function SystemGlobalBanner() {
  const { payload, isBannerDismissed, setDismissedForId } = useSystemGlobalBanner();

  const visible = useMemo(() => {
    if (!payload?.id) return false;
    if (isBannerDismissed) return false;
    return true;
  }, [payload?.id, isBannerDismissed]);

  const handleClose = useCallback(() => {
    if (!payload?.id) return;
    setDismissedForId(payload.id);
  }, [payload?.id, setDismissedForId]);

  const severity = parseSeverity(payload?.config?.severity);
  const iconName = payload?.config?.icon?.trim();
  const IconComp = iconName ? TABLER_ICON_BY_NAME[iconName] : undefined;
  const closable = payload?.config?.closable === true;

  const title = payload?.content?.title?.trim();
  const message = payload?.content?.message?.trim();
  const link = payload?.content?.link;

  if (!visible || !payload) {
    return null;
  }

  return (
    <Box
      sx={{
        maxWidth: 'min(100%, 520px)',
        minWidth: 0,
        flex: '1 1 auto',
        '& .MuiAlert-root': { width: '100%' },
      }}
    >
      <Alert
        severity={severity}
        variant="standard"
        icon={IconComp ? <IconComp stroke={1.5} size={18} /> : undefined}
        onClose={closable ? handleClose : undefined}
        sx={{
          py: 0,
          px: 1,
          alignItems: 'center',
          '& .MuiAlert-message': {
            py: 0.5,
            minWidth: 0,
            overflow: 'hidden',
          },
          '& .MuiAlert-action': { py: 0, alignItems: 'center' },
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          {(title || message) && (
            <Typography component="span" variant="caption" sx={{ lineHeight: 1.4 }}>
              {title && (
                <Box component="span" sx={{ fontWeight: 600, mr: title && message ? 0.5 : 0 }}>
                  {title}
                </Box>
              )}
              {message}
            </Typography>
          )}
          {link?.url && link.text && (
            <Button
              component={Link}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              sx={{ py: 0, minHeight: 22, fontSize: '0.7rem', flexShrink: 0 }}
            >
              {link.text}
            </Button>
          )}
        </Box>
      </Alert>
    </Box>
  );
}
